import { faChevronRight, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import Chip from '@/components/Chip';
import EmptyState from '@/components/EmptyState';
import IconicButton from '@/components/IconicButton';
import LoadingState from '@/components/LoadingState';
import Modal from '@/components/Modal';
import Page from '@/components/Page';
import Skeleton from '@/components/Skeleton';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import { Body, Heading, Small } from '@/components/Typography';
import { TableCell, TableRow } from '@/elements/Table';
import Link from '@/elements/Link';
import ROUTES from '@/routes/routes';

import {
  useAccountDetail,
  useAssignTimesheets,
  useCycleAnalysis,
  useCreateInvoice,
  useCycleAccounts,
  useCycles,
  useReconcile,
  useRemoveLineItem,
  useVoidInvoice,
} from './hooks/useInvoiceHealth';
import type {
  ReconcileAction,
  ReconcilePreview,
} from './hooks/useInvoiceHealth';
import type {
  CycleAccount,
  CycleAccountsData,
  CycleAnalysisResult,
  CycleInvoiceSection,
  CycleTimesheet,
  CreateInvoiceGroup,
  DeepAccountDetail,
  InvoiceCycle,
} from './types';

const humanizeStatus = (status: string) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const humanizeLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatDateTime = (value: string | null) => {
  if (!value) return 'n/a';

  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatCurrency = (amountCents: number | null) => {
  if (amountCents == null) return 'n/a';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amountCents / 100);
};

const statusColor = (
  status: string,
): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'included') return 'success';
  if (status === 'not_included') return 'warning';
  if (
    status === 'requires_review' ||
    status === 'rejected' ||
    status === 'not_invoiceable' ||
    status === 'held'
  ) {
    return 'error';
  }

  return 'default';
};

const linkedTextCss = {
  color: '#16a34a',
  textDecoration: 'underline',
  fontWeight: 600,
};

const flattenAccountTimesheets = (account: CycleAccount) => {
  const seen = new Set<number>();
  const rows: CycleTimesheet[] = [];

  account.invoices.forEach((invoice) => {
    invoice.timesheets.forEach((timesheet) => {
      if (seen.has(timesheet.id)) return;
      seen.add(timesheet.id);
      rows.push(timesheet);
    });
  });

  account.unassigned_timesheets.forEach((timesheet) => {
    if (seen.has(timesheet.id)) return;
    seen.add(timesheet.id);
    rows.push(timesheet);
  });

  return rows;
};

const INVOICE_HEALTH_PASSWORD = 'gravydevs123';
const INVOICE_HEALTH_AUTH_KEY = 'invoiceHealthAuth';

type AccountFilter = 'all' | 'complete' | 'incomplete' | 'no_invoice';

type CycleDropdownProps = {
  cycles: InvoiceCycle[];
  selectedCycle: InvoiceCycle | null;
  cycleData?: CycleAccountsData | null;
  onSelect: (cycle: InvoiceCycle) => void;
};

const CycleDropdown: React.FC<CycleDropdownProps> = ({
  cycles,
  selectedCycle,
  cycleData,
  onSelect,
}) => {
  const selected = selectedCycle;
  const selectedPreviewStats = useMemo(() => {
    if (!selected?.is_preview || !cycleData) return null;

    return {
      invoiceCount: cycleData.accounts.reduce(
        (sum, account) => sum + account.invoices.length,
        0,
      ),
      timesheetCount: cycleData.summary.timesheets_count,
      failedAccountCount: selected.failed_account_count,
    };
  }, [cycleData, selected]);

  const displayInvoiceCount =
    selectedPreviewStats?.invoiceCount ?? selected?.invoice_count ?? 0;
  const displayTimesheetCount =
    selectedPreviewStats?.timesheetCount ?? selected?.timesheet_count ?? 0;
  const displayFailedAccountCount =
    selectedPreviewStats?.failedAccountCount ??
    selected?.failed_account_count ??
    0;

  return (
    <Stack vertical gap={8}>
      <div style={{ minWidth: 320 }}>
        <select
          value={selected?.id ?? ''}
          onChange={(event) => {
            const cycle = cycles.find(
              (entry) => entry.id === Number(event.target.value),
            );
            if (cycle) onSelect(cycle);
          }}
          style={{
            width: '100%',
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid #d0d5dd',
            background: '#fff',
            fontSize: 14,
            fontWeight: 500,
            color: '#344054',
            cursor: 'pointer',
            appearance: 'none',
          }}
        >
          <option value="" disabled>
            Select an invoice cycle...
          </option>
          {cycles.map((cycle) => (
            <option key={cycle.id} value={cycle.id}>
              {cycle.is_preview ? 'Future: ' : ''}
              {cycle.date_label} ({cycle.day_label})
            </option>
          ))}
        </select>
      </div>

      {selected && (
        <Stack vertical gap={4}>
          <Stack gap={8} align="center" wrap>
            <Small>
              {displayInvoiceCount} invoices and {displayTimesheetCount}{' '}
              timesheets in cycle
            </Small>
            {displayFailedAccountCount > 0 && (
              <div style={{ color: '#b42318', fontSize: 12 }}>
                {displayFailedAccountCount} failed accounts
              </div>
            )}
          </Stack>
          {selected.is_preview && (
            <Small>
              Previewing next week&apos;s run based on{' '}
              {selected.preview_base_label || 'the selected cycle'}
            </Small>
          )}
        </Stack>
      )}
    </Stack>
  );
};

type ConfirmCreateState = {
  account: CycleAccount;
} | null;

type ConfirmCreateModalProps = {
  state: ConfirmCreateState;
  groups: CreateInvoiceGroup[];
  loading: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (groupKeys: string[]) => void;
};

const ConfirmCreateInvoiceModal: React.FC<ConfirmCreateModalProps> = ({
  state,
  groups,
  loading,
  error,
  onCancel,
  onConfirm,
}) => {
  const [selectedGroupKeys, setSelectedGroupKeys] = useState<string[]>([]);

  useEffect(() => {
    setSelectedGroupKeys(groups.map((group) => group.group_key));
  }, [groups, state]);

  if (!state) return null;

  const toggleGroup = (groupKey: string) => {
    setSelectedGroupKeys((current) =>
      current.includes(groupKey)
        ? current.filter((value) => value !== groupKey)
        : [...current, groupKey],
    );
  };

  return (
    <Modal
      displayFlex
      size="md"
      title="Create grouped invoices"
      onRequestClose={loading ? undefined : onCancel}
      wrapperBackground
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            <Body weight="semibold">
              Choose which missing invoice groups to create for{' '}
              {state.account.account_name}.
            </Body>
            <Small>
              Invoice creation follows the account&apos;s existing grouping
              rules. Each selected group will create one invoice bucket for the
              current cycle.
            </Small>
            {groups.length === 0 ? (
              <EmptyState title="No missing invoice groups to create" />
            ) : (
              <Stack vertical gap={10}>
                {groups.map((group) => (
                  <label
                    key={group.group_key}
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'flex-start',
                      padding: 12,
                      border: '1px solid #e4e7ec',
                      borderRadius: 10,
                      background: '#f8fafc',
                    }}
                  >
                    <input
                      checked={selectedGroupKeys.includes(group.group_key)}
                      disabled={loading}
                      type="checkbox"
                      onChange={() => toggleGroup(group.group_key)}
                    />
                    <Stack vertical gap={4} css={{ flex: 1 }}>
                      <Body weight="semibold">{group.group_label}</Body>
                      <Small>
                        {group.timesheets_count} timesheets •{' '}
                        {formatCurrency(group.total_amount_cents)}
                      </Small>
                    </Stack>
                  </label>
                ))}
              </Stack>
            )}
            {error && (
              <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
            )}
            <Stack justify="end" gap={12}>
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                disabled={loading}
                type="button"
                onClick={onCancel}
              />
              <Button
                a11yLabel="Confirm create invoice"
                label="Create selected"
                disabled={groups.length === 0 || selectedGroupKeys.length === 0}
                isLoading={loading}
                type="button"
                onClick={() => onConfirm(selectedGroupKeys)}
              />
            </Stack>
          </Stack>
        </Card.Section>
      </Card>
    </Modal>
  );
};

const ACTION_LABELS: Record<string, string> = {
  void_duplicate: 'Void duplicate invoice',
  fix_totals: 'Sync totals from Stripe',
  items_missing_locally: 'Create missing local items',
  items_orphaned_locally: 'Remove orphaned local items',
  fix_item_amount: 'Fix item amount mismatch',
  stripe_invoice_not_found: 'Stripe invoice not found',
  error: 'Error during reconciliation',
};

const actionLabel = (action: string) =>
  ACTION_LABELS[action] || humanizeLabel(action);

const formatReconcileDetailValue = (value: unknown) => {
  if (value == null) return 'n/a';
  if (Array.isArray(value)) {
    return value.map((entry) => formatReconcileDetailValue(entry)).join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

type ReconcileModalProps = {
  isOpen: boolean;
  accountName: string | null;
  accountId: number | null;
  preview: ReconcilePreview | null;
  loading: boolean;
  executing: boolean;
  error: string | null;
  onClose: () => void;
  onExecute: () => void;
};

const ReconcileModal: React.FC<ReconcileModalProps> = ({
  isOpen,
  accountName,
  accountId,
  preview,
  loading,
  executing,
  error,
  onClose,
  onExecute,
}) => {
  if (!isOpen) return null;

  const actions = preview?.actions || [];
  const hasActions = actions.length > 0;
  const isExecuted = preview?.mode === 'execute';

  return (
    <Modal
      disableClickout
      size="lg"
      title={`Reconcile ${accountName || `Account ${accountId}`}`}
      onRequestClose={executing ? undefined : onClose}
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={6}>
            <Body weight="semibold">
              {isExecuted
                ? 'Reconciliation completed'
                : 'Review the Stripe sync preview before making changes'}
            </Body>
            <Small>
              {isExecuted
                ? 'Executed actions are listed below so you can review what changed.'
                : 'This checks the selected account against Stripe and proposes safe repair actions before you execute them.'}
            </Small>
          </Stack>
        </Card.Section>

        <Card.Section>
          {loading && (
            <LoadingState text="Scanning invoices against Stripe..." />
          )}
          {!loading && error && (
            <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
          )}
          {!loading && preview && (
            <Stack vertical gap={16}>
              <Card>
                <Card.Section>
                  <Stack vertical gap={10}>
                    <Body weight="semibold">Summary</Body>
                    <Stack gap={8} wrap>
                      <Chip
                        color="default"
                        label={`${preview.invoices_scanned} invoices scanned`}
                        size="small"
                      />
                      {Object.entries(preview.stats).map(([key, value]) =>
                        value > 0 ? (
                          <Chip
                            key={key}
                            color="default"
                            label={`${humanizeLabel(key)} ${value}`}
                            size="small"
                          />
                        ) : null,
                      )}
                    </Stack>
                    <Small>
                      Mode:{' '}
                      {isExecuted ? 'Executed changes' : 'Dry run preview only'}
                    </Small>
                  </Stack>
                </Card.Section>
              </Card>

              {!hasActions && (
                <Card>
                  <Card.Section>
                    <EmptyState title="No reconciliation actions needed — everything is in sync" />
                  </Card.Section>
                </Card>
              )}

              {hasActions && (
                <Card>
                  <Card.Section>
                    <Body weight="semibold">
                      {isExecuted ? 'Actions executed' : 'Proposed actions'} (
                      {actions.length})
                    </Body>
                  </Card.Section>
                  <Card.Section>
                    <Stack vertical gap={8}>
                      {actions.map((action: ReconcileAction, idx: number) => (
                        <div
                          key={idx}
                          style={{
                            border: '1px solid #eaecf0',
                            borderRadius: 10,
                            padding: 12,
                            background: '#f8fafc',
                          }}
                        >
                          <Stack gap={8} align="center" wrap>
                            <Chip
                              color={
                                isExecuted
                                  ? 'success'
                                  : action.action === 'error'
                                    ? 'error'
                                    : 'warning'
                              }
                              label={actionLabel(action.action)}
                              size="small"
                            />
                            <Small>Invoice #{action.invoice_id}</Small>
                            {action.stripe_invoice_id && (
                              <Small>Stripe {action.stripe_invoice_id}</Small>
                            )}
                          </Stack>
                          <Stack vertical gap={4} css={{ marginTop: 8 }}>
                            {Object.keys(action.details || {}).length > 0 ? (
                              Object.entries(action.details).map(
                                ([key, value]) => (
                                  <Small key={key}>
                                    <strong>{humanizeLabel(key)}:</strong>{' '}
                                    {formatReconcileDetailValue(value)}
                                  </Small>
                                ),
                              )
                            ) : (
                              <Small>No extra details provided.</Small>
                            )}
                          </Stack>
                        </div>
                      ))}
                    </Stack>
                  </Card.Section>
                </Card>
              )}

              <Stack justify="end" gap={12}>
                <Button
                  a11yLabel="Close"
                  appearance="outline"
                  label={isExecuted ? 'Done' : 'Cancel'}
                  disabled={executing}
                  type="button"
                  onClick={onClose}
                />
                {hasActions && !isExecuted && (
                  <Button
                    a11yLabel="Execute reconciliation"
                    label="Execute reconciliation"
                    isLoading={executing}
                    type="button"
                    onClick={onExecute}
                  />
                )}
              </Stack>
            </Stack>
          )}
        </Card.Section>
      </Card>
    </Modal>
  );
};

type ReconcileState = {
  accountId: number;
  accountName: string;
} | null;

type DetailState = CycleAccount | null;
type CycleAnalysisState = {
  cycleLabel: string;
} | null;

type AccountDetailModalProps = {
  isOpen: boolean;
  loading: boolean;
  error: string | null;
  account: CycleAccount | null;
  accountDetail: DeepAccountDetail | null;
  onClose: () => void;
};

const AccountDetailModal: React.FC<AccountDetailModalProps> = ({
  isOpen,
  loading,
  error,
  account,
  accountDetail,
  onClose,
}) => {
  if (!isOpen || !account) return null;

  const summaryEntries = Object.entries(accountDetail?.summary || {});
  const duplicateInvoices =
    accountDetail?.duplicate_local_invoices_by_stripe_invoice_id || [];
  const duplicateItems =
    accountDetail?.duplicate_local_invoice_items_by_stripe_invoice_item_id ||
    [];

  return (
    <Modal
      disableClickout
      size="lg"
      title={`Account details for ${account.account_name}`}
      onRequestClose={onClose}
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={6}>
            <Body weight="semibold">
              Review invoice coverage for this account
            </Body>
            <Small>
              Customer {account.customer_name} (#{account.customer_id}) and
              account #{account.account_id}
            </Small>
          </Stack>
        </Card.Section>

        <Card.Section>
          {loading && <LoadingState text="Loading account details..." />}
          {!loading && error && (
            <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
          )}
          {!loading && accountDetail && (
            <Stack vertical gap={16}>
              <Card>
                <Card.Section>
                  <Stack vertical gap={10}>
                    <Body weight="semibold">Summary</Body>
                    <Stack gap={8} wrap>
                      <Chip
                        color="default"
                        label={`${accountDetail.analyzed_timesheet_ids.length} timesheets analyzed`}
                        size="small"
                      />
                      <Chip
                        color="default"
                        label={`${accountDetail.invoices.length} related invoices`}
                        size="small"
                      />
                      <Chip
                        color={
                          duplicateInvoices.length > 0 ? 'warning' : 'success'
                        }
                        label={`${duplicateInvoices.length} duplicate invoices`}
                        size="small"
                      />
                      <Chip
                        color={
                          duplicateItems.length > 0 ? 'warning' : 'success'
                        }
                        label={`${duplicateItems.length} duplicate invoice items`}
                        size="small"
                      />
                    </Stack>
                    {summaryEntries.length > 0 && (
                      <Stack gap={8} wrap>
                        {summaryEntries.map(([status, count]) => (
                          <Chip
                            key={status}
                            color="default"
                            label={`${humanizeLabel(status)} ${count}`}
                            size="small"
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              <Card>
                <Card.Section>
                  <Body weight="semibold">
                    Timesheets ({accountDetail.timesheets.length})
                  </Body>
                </Card.Section>
                <Card.Section>
                  <Stack vertical gap={8}>
                    {accountDetail.timesheets.length === 0 ? (
                      <Small>No timesheets returned for this account.</Small>
                    ) : (
                      accountDetail.timesheets.map((timesheetDetail) => (
                        <div
                          key={timesheetDetail.timesheet.id}
                          style={{
                            border: '1px solid #eaecf0',
                            borderRadius: 10,
                            padding: 12,
                            background: '#f8fafc',
                          }}
                        >
                          <Stack gap={8} align="center" wrap>
                            <Body weight="semibold">
                              Timesheet {timesheetDetail.timesheet.id}
                            </Body>
                            <Chip
                              color={statusColor(timesheetDetail.status)}
                              label={humanizeLabel(timesheetDetail.status)}
                              size="small"
                            />
                            <Chip
                              color="default"
                              label={humanizeLabel(
                                timesheetDetail.recommended_action,
                              )}
                              size="small"
                            />
                          </Stack>
                          <Stack vertical gap={4} css={{ marginTop: 8 }}>
                            <Small>
                              Active invoice items:{' '}
                              {timesheetDetail.active_invoice_items.length}
                            </Small>
                            {timesheetDetail.net_time_analysis && (
                              <Small>
                                Net time:{' '}
                                {
                                  timesheetDetail.net_time_analysis
                                    .expected_net_time
                                }
                                min expected,{' '}
                                {
                                  timesheetDetail.net_time_analysis
                                    .actual_net_time
                                }
                                min actual
                              </Small>
                            )}
                            {timesheetDetail.issues.length > 0 && (
                              <Stack gap={8} wrap>
                                {timesheetDetail.issues.map((issue) => (
                                  <Chip
                                    key={issue}
                                    color="warning"
                                    label={humanizeLabel(issue)}
                                    size="small"
                                  />
                                ))}
                              </Stack>
                            )}
                          </Stack>
                        </div>
                      ))
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              <Card>
                <Card.Section>
                  <Body weight="semibold">
                    Related invoices ({accountDetail.invoices.length})
                  </Body>
                </Card.Section>
                <Card.Section>
                  <Stack vertical gap={8}>
                    {accountDetail.invoices.length === 0 ? (
                      <Small>No related invoices found.</Small>
                    ) : (
                      accountDetail.invoices.map((invoice) => (
                        <div
                          key={invoice.invoice.id}
                          style={{
                            border: '1px solid #eaecf0',
                            borderRadius: 10,
                            padding: 12,
                            background: '#f8fafc',
                          }}
                        >
                          <Stack gap={8} align="center" wrap>
                            <Body weight="semibold">
                              Invoice #{invoice.invoice.id}
                            </Body>
                            <Chip
                              color="default"
                              label={humanizeLabel(invoice.status)}
                              size="small"
                            />
                            {invoice.invoice.stripe_invoice_id && (
                              <Small>
                                Stripe {invoice.invoice.stripe_invoice_id}
                              </Small>
                            )}
                          </Stack>
                          <Stack vertical gap={4} css={{ marginTop: 8 }}>
                            <Small>
                              Local items: {invoice.local_invoice_items.length}
                            </Small>
                            <Small>
                              Stripe items:{' '}
                              {invoice.stripe_invoice_items.length}
                            </Small>
                            {invoice.issues.length > 0 && (
                              <Stack gap={8} wrap>
                                {invoice.issues.map((issue) => (
                                  <Chip
                                    key={issue}
                                    color="warning"
                                    label={humanizeLabel(issue)}
                                    size="small"
                                  />
                                ))}
                              </Stack>
                            )}
                          </Stack>
                        </div>
                      ))
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              {(duplicateInvoices.length > 0 || duplicateItems.length > 0) && (
                <Card>
                  <Card.Section>
                    <Body weight="semibold">Duplicates</Body>
                  </Card.Section>
                  <Card.Section>
                    <Stack vertical gap={8}>
                      {duplicateInvoices.map((duplicate) => (
                        <Small key={duplicate.stripe_invoice_id}>
                          Stripe invoice {duplicate.stripe_invoice_id}: local
                          invoices {duplicate.invoice_ids.join(', ')}
                        </Small>
                      ))}
                      {duplicateItems.map((duplicate) => (
                        <Small key={duplicate.stripe_invoice_item_id}>
                          Stripe invoice item {duplicate.stripe_invoice_item_id}
                          : local items {duplicate.invoice_item_ids.join(', ')}
                        </Small>
                      ))}
                    </Stack>
                  </Card.Section>
                </Card>
              )}

              <Stack justify="end" gap={12}>
                <Button
                  a11yLabel="Close account details"
                  appearance="outline"
                  label="Close"
                  type="button"
                  onClick={onClose}
                />
              </Stack>
            </Stack>
          )}
        </Card.Section>
      </Card>
    </Modal>
  );
};

type CycleAnalysisModalProps = {
  isOpen: boolean;
  cycleLabel: string | null;
  loading: boolean;
  error: string | null;
  analysis: CycleAnalysisResult | null;
  onClose: () => void;
};

const CycleAnalysisModal: React.FC<CycleAnalysisModalProps> = ({
  isOpen,
  cycleLabel,
  loading,
  error,
  analysis,
  onClose,
}) => {
  if (!isOpen) return null;

  const summary = analysis?.summary;
  const problems = analysis?.problematic_timesheets || [];

  return (
    <Modal
      disableClickout
      size="lg"
      title={`Invoice analysis${cycleLabel ? ` for ${cycleLabel}` : ''}`}
      onRequestClose={onClose}
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={6}>
            <Body weight="semibold">
              Review cycle completeness across all approved timesheets
            </Body>
            <Small>
              This checks whether approved cycle timesheets are attached to a
              valid non-void, non-deleted invoice and flags uninvoiced or stuck
              rows.
            </Small>
          </Stack>
        </Card.Section>

        <Card.Section>
          {loading && <LoadingState text="Running invoice analysis..." />}
          {!loading && error && (
            <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
          )}
          {!loading && analysis && (
            <Stack vertical gap={16}>
              <Card>
                <Card.Section>
                  <Stack vertical gap={10}>
                    <Body weight="semibold">Summary</Body>
                    <Stack gap={8} wrap>
                      <Chip
                        color={summary.all_complete ? 'success' : 'warning'}
                        label={
                          summary.all_complete
                            ? 'All timesheets complete'
                            : `${summary.problematic_timesheets_count} problematic timesheets`
                        }
                        size="small"
                      />
                      <Chip
                        color="default"
                        label={`${summary.accounts_count} accounts`}
                        size="small"
                      />
                      <Chip
                        color="default"
                        label={`${summary.timesheets_count} timesheets`}
                        size="small"
                      />
                      <Chip
                        color="success"
                        label={`${summary.complete_timesheets_count} complete`}
                        size="small"
                      />
                      <Chip
                        color="warning"
                        label={`${summary.uninvoiced_timesheets_count} uninvoiced`}
                        size="small"
                      />
                      <Chip
                        color="error"
                        label={`${summary.stuck_timesheets_count} stuck`}
                        size="small"
                      />
                    </Stack>
                    {Object.entries(analysis.stats).length > 0 && (
                      <Stack gap={8} wrap>
                        {Object.entries(analysis.stats).map(([key, value]) => (
                          <Chip
                            key={key}
                            color="default"
                            label={`${humanizeLabel(key)} ${value}`}
                            size="small"
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Card.Section>
              </Card>

              {problems.length === 0 ? (
                <Card>
                  <Card.Section>
                    <EmptyState title="No uninvoiced or stuck timesheets found" />
                  </Card.Section>
                </Card>
              ) : (
                <Card>
                  <Card.Section>
                    <Body weight="semibold">
                      Problematic timesheets ({problems.length})
                    </Body>
                  </Card.Section>
                  <Card.Section>
                    <Stack vertical gap={8}>
                      {problems.map((problem) => {
                        const invoiceUrl = problem.linked_invoice_id
                          ? `/${ROUTES.invoices}/${problem.linked_invoice_id}`
                          : null;

                        return (
                          <div
                            key={problem.id}
                            style={{
                              border: '1px solid #eaecf0',
                              borderRadius: 10,
                              padding: 12,
                              background: '#f8fafc',
                            }}
                          >
                            <Stack gap={8} align="center" wrap>
                              <Body weight="semibold">
                                Timesheet {problem.id}
                              </Body>
                              <Chip
                                color={statusColor(problem.status)}
                                label={humanizeStatus(problem.status)}
                                size="small"
                              />
                              {problem.problem_labels.map((label) => (
                                <Chip
                                  key={`${problem.id}-${label}`}
                                  color="warning"
                                  label={label}
                                  size="small"
                                />
                              ))}
                            </Stack>
                            <Stack vertical gap={4} css={{ marginTop: 8 }}>
                              <Small>
                                {problem.customer_name} - {problem.account_name}
                              </Small>
                              <Small>
                                Worker {problem.worker_name || 'n/a'} • Group{' '}
                                {problem.derived_group_label}
                              </Small>
                              <Small>
                                Approved {formatDateTime(problem.approved_at)} •
                                First shift{' '}
                                {formatDateTime(problem.first_shift_starts_at)}
                              </Small>
                              <Small>
                                Amount{' '}
                                {formatCurrency(problem.total_amount_cents)}
                              </Small>
                              {problem.hold_by_invoice_id && (
                                <Small>
                                  Held by invoice #{problem.hold_by_invoice_id}
                                </Small>
                              )}
                              {invoiceUrl ? (
                                <Small>
                                  Linked invoice{' '}
                                  <Link css={linkedTextCss} to={invoiceUrl}>
                                    {problem.linked_invoice_number ||
                                      `#${problem.linked_invoice_id}`}
                                  </Link>
                                  {problem.linked_invoice_status
                                    ? ` • ${humanizeStatus(problem.linked_invoice_status)}`
                                    : ''}
                                </Small>
                              ) : (
                                <Small>No linked invoice</Small>
                              )}
                            </Stack>
                          </div>
                        );
                      })}
                    </Stack>
                  </Card.Section>
                </Card>
              )}

              <Stack justify="end" gap={12}>
                <Button
                  a11yLabel="Close invoice analysis"
                  appearance="outline"
                  label="Close"
                  type="button"
                  onClick={onClose}
                />
              </Stack>
            </Stack>
          )}
        </Card.Section>
      </Card>
    </Modal>
  );
};

type AccountCardProps = {
  account: CycleAccount;
  loadingAssignInvoiceId: number | null;
  loadingRemoveItemId: number | null;
  loadingCreateAccountId: number | null;
  loadingVoidInvoiceId: number | null;
  onAssignTimesheets: (
    account: CycleAccount,
    invoiceId: number,
    timesheetIds: number[],
  ) => Promise<boolean>;
  onRemoveLineItem: (account: CycleAccount, timesheet: CycleTimesheet) => void;
  onInspectInvoice: (account: CycleAccount) => void;
  onCreateInvoice: (account: CycleAccount) => void;
  onVoidInvoice: (account: CycleAccount, invoice: CycleInvoiceSection) => void;
  onReconcile: (account: CycleAccount) => void;
};

const AccountCard: React.FC<AccountCardProps> = ({
  account,
  loadingAssignInvoiceId,
  loadingRemoveItemId,
  loadingCreateAccountId,
  loadingVoidInvoiceId,
  onAssignTimesheets,
  onRemoveLineItem,
  onInspectInvoice,
  onCreateInvoice,
  onVoidInvoice,
  onReconcile,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedTimesheetIds, setSelectedTimesheetIds] = useState<number[]>(
    [],
  );
  const [targetInvoiceId, setTargetInvoiceId] = useState<number | null>(null);
  const conflictedGroups = account.conflicted_groups || [];

  const selectedTimesheets = useMemo(
    () =>
      account.unassigned_timesheets.filter((timesheet) =>
        selectedTimesheetIds.includes(timesheet.id),
      ),
    [account.unassigned_timesheets, selectedTimesheetIds],
  );

  const compatibleInvoices = useMemo(() => {
    if (selectedTimesheets.length === 0) return account.invoices;

    return account.invoices.filter((invoice) =>
      selectedTimesheets.every((timesheet) =>
        timesheet.assignable_invoice_ids.includes(invoice.id),
      ),
    );
  }, [account.invoices, selectedTimesheets]);

  useEffect(() => {
    if (compatibleInvoices.length === 0) {
      setTargetInvoiceId(null);
      return;
    }

    if (
      targetInvoiceId != null &&
      compatibleInvoices.some((invoice) => invoice.id === targetInvoiceId)
    ) {
      return;
    }

    setTargetInvoiceId(compatibleInvoices[0].id);
  }, [compatibleInvoices, targetInvoiceId]);

  useEffect(() => {
    setSelectedTimesheetIds([]);
    setTargetInvoiceId(null);
  }, [account.account_id, account.invoices, account.unassigned_timesheets]);

  const toggleTimesheet = (timesheetId: number) => {
    setSelectedTimesheetIds((current) =>
      current.includes(timesheetId)
        ? current.filter((value) => value !== timesheetId)
        : [...current, timesheetId],
    );
  };

  const renderTimesheetTable = (
    timesheets: CycleTimesheet[],
    options: { invoice?: CycleInvoiceSection; showSelection?: boolean } = {},
  ) => {
    const { invoice, showSelection = false } = options;

    return (
      <div style={{ overflowX: 'auto' }}>
        <Table>
          <TableHeader
            fields={[
              ...(showSelection ? [{ name: '', style: { width: 56 } }] : []),
              { name: 'TIMESHEET', style: { width: 90 } },
              { name: 'WORKER', style: { width: 180 } },
              { name: 'GROUP', style: { width: 220 } },
              { name: 'FIRST SHIFT STARTS', style: { width: 210 } },
              { name: 'STATUS', style: { width: 180 } },
              { name: 'APPROVED AT', style: { width: 210 } },
              { name: 'TOTAL AMOUNT', style: { width: 140 } },
              { name: 'LINE ITEM', style: { width: 220 } },
              { name: 'INVOICE', style: { width: 170 } },
              { name: '', style: { width: 160 } },
            ]}
          />
          <tbody>
            {timesheets.map((timesheet) => {
              const timesheetUrl =
                timesheet.order_id != null && timesheet.job_id != null
                  ? `/${ROUTES.orders}/${timesheet.order_id}/jobs/${timesheet.job_id}`
                  : null;
              const linkedInvoiceUrl = timesheet.linked_invoice_id
                ? `/${ROUTES.invoices}/${timesheet.linked_invoice_id}`
                : null;
              const canSelect =
                showSelection &&
                timesheet.status === 'not_included' &&
                timesheet.assignable_invoice_ids.length > 0;
              const isSelected = selectedTimesheetIds.includes(timesheet.id);

              return (
                <TableRow key={timesheet.id}>
                  {showSelection && (
                    <TableCell>
                      <input
                        checked={isSelected}
                        disabled={!canSelect}
                        type="checkbox"
                        onChange={() => toggleTimesheet(timesheet.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {timesheetUrl ? (
                      <Link css={linkedTextCss} to={timesheetUrl}>
                        <Body weight="semibold">{timesheet.id}</Body>
                      </Link>
                    ) : (
                      <Body css={linkedTextCss} weight="semibold">
                        {timesheet.id}
                      </Body>
                    )}
                  </TableCell>
                  <TableCell>
                    {timesheet.worker_id && timesheet.worker_name ? (
                      <Link
                        css={linkedTextCss}
                        to={`/${ROUTES.workersV2}/${timesheet.worker_id}`}
                      >
                        <Small>{timesheet.worker_name}</Small>
                      </Link>
                    ) : (
                      <Small>{timesheet.worker_name || 'n/a'}</Small>
                    )}
                  </TableCell>
                  <TableCell>
                    <Small>{timesheet.derived_group_label}</Small>
                  </TableCell>
                  <TableCell>
                    <Small>
                      {formatDateTime(timesheet.first_shift_starts_at)}
                    </Small>
                  </TableCell>
                  <TableCell>
                    <Chip
                      color={statusColor(timesheet.status)}
                      label={humanizeStatus(timesheet.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Small>{formatDateTime(timesheet.approved_at)}</Small>
                  </TableCell>
                  <TableCell>
                    <Small>
                      {formatCurrency(timesheet.total_amount_cents)}
                    </Small>
                  </TableCell>
                  <TableCell>
                    {timesheet.invoice_line_item_id ? (
                      <Small>Line item #{timesheet.invoice_line_item_id}</Small>
                    ) : (
                      <Small>No line item</Small>
                    )}
                  </TableCell>
                  <TableCell>
                    {linkedInvoiceUrl ? (
                      <Link css={linkedTextCss} to={linkedInvoiceUrl}>
                        <Small>
                          {timesheet.linked_invoice_number ||
                            `#${timesheet.linked_invoice_id}`}
                        </Small>
                      </Link>
                    ) : invoice ? (
                      <Small>Invoice #{invoice.id}</Small>
                    ) : (
                      <Small>Not assigned</Small>
                    )}
                  </TableCell>
                  <TableCell>
                    {invoice && timesheet.invoice_line_item_id ? (
                      <Button
                        a11yLabel={`Remove line item for timesheet ${timesheet.id}`}
                        appearance="outline"
                        label="Remove"
                        isLoading={
                          loadingRemoveItemId === timesheet.invoice_line_item_id
                        }
                        type="button"
                        onClick={() => onRemoveLineItem(account, timesheet)}
                      />
                    ) : (
                      <Small>
                        {timesheet.status === 'not_included'
                          ? 'Ready to assign'
                          : humanizeStatus(timesheet.status)}
                      </Small>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <Card>
      <Card.Section>
        <Stack justify="apart" align="start" gap={16}>
          <Stack vertical gap={6} css={{ flex: 1, minWidth: 0 }}>
            <Stack gap={8} align="center" wrap>
              <Heading>
                {account.customer_name} - {account.account_name}
              </Heading>
              <Chip
                color="default"
                label={`${account.timesheets_count} timesheets`}
                size="small"
              />
              <Chip
                color="default"
                label={`${account.invoices.length} invoices`}
                size="small"
              />
              {conflictedGroups.length > 0 && (
                <Chip
                  color="error"
                  label={`${conflictedGroups.length} conflicted groups`}
                  size="small"
                />
              )}
            </Stack>
            {!isExpanded && (
              <Small>
                {account.invoices.length > 0
                  ? `${account.invoices.length} linked invoices • ${account.missing_timesheets_count} unassigned`
                  : `${account.missing_timesheets_count} unassigned timesheets`}
              </Small>
            )}
            {isExpanded && (
              <>
                <Stack gap={10} wrap>
                  <div
                    style={{
                      padding: '8px 12px',
                      borderRadius: 10,
                      background: '#f8fafc',
                      border: '1px solid #e4e7ec',
                      minWidth: 180,
                    }}
                  >
                    <Small>Customer</Small>
                    <Body weight="semibold">{account.customer_name}</Body>
                    <Small>ID {account.customer_id}</Small>
                  </div>
                  <div
                    style={{
                      padding: '8px 12px',
                      borderRadius: 10,
                      background: '#f8fafc',
                      border: '1px solid #e4e7ec',
                      minWidth: 160,
                    }}
                  >
                    <Small>Account</Small>
                    <Body weight="semibold">{account.account_name}</Body>
                    <Small>ID {account.account_id}</Small>
                  </div>
                </Stack>
                {conflictedGroups.length > 0 && (
                  <Stack vertical gap={6}>
                    {conflictedGroups.map((group) => (
                      <Small key={group.group_key} style={{ color: '#b42318' }}>
                        {group.group_label}: invoices{' '}
                        {group.invoice_ids.join(', ')}
                      </Small>
                    ))}
                  </Stack>
                )}
              </>
            )}
          </Stack>

          <Stack inline>
            <IconicButton
              a11yLabel={`${isExpanded ? 'Collapse' : 'Expand'} account ${account.account_id}`}
              appearance="clear"
              css={{ fontSize: '1rem', marginLeft: 'auto' }}
              icon={isExpanded ? faChevronUp : faChevronRight}
              iconTheme="theme"
              rounded
              size="sm"
              type="button"
              onClick={() => setIsExpanded((current) => !current)}
            />
          </Stack>
        </Stack>
      </Card.Section>

      {isExpanded && (
        <Card.Section>
          <Stack gap={8} wrap>
            <Button
              a11yLabel={`Create invoice for account ${account.account_id}`}
              appearance="secondary"
              label="Create invoice"
              isLoading={loadingCreateAccountId === account.account_id}
              type="button"
              onClick={() => onCreateInvoice(account)}
            />
            {account.invoices.length > 0 && (
              <Button
                a11yLabel={`Reconcile invoice for account ${account.account_id}`}
                appearance="secondary"
                label="Reconcile"
                type="button"
                onClick={() => onReconcile(account)}
              />
            )}
            <Button
              a11yLabel={`Explore invoice details for account ${account.account_id}`}
              appearance="secondary"
              label="Explore more details"
              type="button"
              onClick={() => onInspectInvoice(account)}
            />
          </Stack>
        </Card.Section>
      )}

      {isExpanded && (
        <Card.Section>
          <Stack vertical gap={16}>
            {account.invoices.map((invoice) => {
              const invoiceUrl = `/${ROUTES.invoices}/${invoice.id}`;
              const stripeReference =
                invoice.number || invoice.stripe_invoice_id || null;
              const canVoid =
                invoice.status === 'open' && !invoice.duplicate_in_group;

              return (
                <Card key={invoice.id}>
                  <Card.Section>
                    <Stack justify="apart" align="start" gap={16}>
                      <Stack vertical gap={6}>
                        <Stack gap={8} align="center" wrap>
                          <Body weight="semibold">
                            <Link css={linkedTextCss} to={invoiceUrl}>
                              Invoice #{invoice.id}
                            </Link>
                          </Body>
                          <Chip
                            color="default"
                            label={invoice.group_label}
                            size="small"
                          />
                          <Chip
                            color="default"
                            label={invoice.status}
                            size="small"
                          />
                          {invoice.duplicate_in_group && (
                            <Chip
                              color="error"
                              label="Conflicted group"
                              size="small"
                            />
                          )}
                        </Stack>
                        <Small>
                          {invoice.timesheets_count} timesheets •{' '}
                          {formatCurrency(invoice.total_amount_cents)}
                        </Small>
                        {stripeReference && (
                          <Small>Stripe reference: {stripeReference}</Small>
                        )}
                      </Stack>
                      {canVoid && (
                        <Button
                          a11yLabel={`Void invoice ${invoice.id}`}
                          appearance="outline"
                          label="Void invoice"
                          isLoading={loadingVoidInvoiceId === invoice.id}
                          type="button"
                          onClick={() => onVoidInvoice(account, invoice)}
                        />
                      )}
                    </Stack>
                  </Card.Section>
                  <Card.Section>
                    {invoice.timesheets.length === 0 ? (
                      <Small>No timesheets are currently assigned.</Small>
                    ) : (
                      renderTimesheetTable(invoice.timesheets, { invoice })
                    )}
                  </Card.Section>
                </Card>
              );
            })}

            <Card>
              <Card.Section>
                <Stack justify="apart" align="start" gap={16}>
                  <Stack vertical gap={6}>
                    <Body weight="semibold">Unassigned Timesheets</Body>
                    <Small>
                      {account.unassigned_timesheets.length} timesheets not
                      currently attached to a cycle invoice
                    </Small>
                  </Stack>
                  {selectedTimesheetIds.length > 0 && (
                    <Stack gap={8} align="center" wrap>
                      <Small>{selectedTimesheetIds.length} selected</Small>
                      <select
                        value={targetInvoiceId ?? ''}
                        onChange={(event) =>
                          setTargetInvoiceId(Number(event.target.value))
                        }
                        style={{
                          minWidth: 220,
                          padding: '10px 14px',
                          borderRadius: 8,
                          border: '1px solid #d0d5dd',
                          background: '#fff',
                          fontSize: 14,
                        }}
                      >
                        {compatibleInvoices.map((invoice) => (
                          <option key={invoice.id} value={invoice.id}>
                            Invoice #{invoice.id} • {invoice.group_label}
                          </option>
                        ))}
                      </select>
                      <Button
                        a11yLabel={`Assign selected timesheets for account ${account.account_id}`}
                        disabled={
                          targetInvoiceId == null ||
                          compatibleInvoices.length === 0
                        }
                        appearance="secondary"
                        label="Assign to invoice"
                        isLoading={loadingAssignInvoiceId === targetInvoiceId}
                        type="button"
                        onClick={() => {
                          if (targetInvoiceId == null) return;
                          void onAssignTimesheets(
                            account,
                            targetInvoiceId,
                            selectedTimesheetIds,
                          ).then((success) => {
                            if (success) {
                              setSelectedTimesheetIds([]);
                            }
                          });
                        }}
                      />
                    </Stack>
                  )}
                </Stack>
              </Card.Section>
              <Card.Section>
                {account.unassigned_timesheets.length === 0 ? (
                  <Small>No unassigned timesheets in this cycle.</Small>
                ) : (
                  renderTimesheetTable(account.unassigned_timesheets, {
                    showSelection: true,
                  })
                )}
              </Card.Section>
            </Card>
          </Stack>
        </Card.Section>
      )}
    </Card>
  );
};

const InvoiceHealth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem(INVOICE_HEALTH_AUTH_KEY) === 'true';
  });
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [selectedCycle, setSelectedCycle] = useState<InvoiceCycle | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [accountFilter, setAccountFilter] = useState<AccountFilter>('all');
  const [confirmCreateState, setConfirmCreateState] =
    useState<ConfirmCreateState>(null);
  const [cycleAnalysisState, setCycleAnalysisState] =
    useState<CycleAnalysisState>(null);
  const [reconcileState, setReconcileState] = useState<ReconcileState>(null);
  const [detailState, setDetailState] = useState<DetailState>(null);
  const cycles = useCycles();
  const cycleAccounts = useCycleAccounts();
  const cycleAnalysis = useCycleAnalysis();
  const assignTimesheets = useAssignTimesheets();
  const removeLineItem = useRemoveLineItem();
  const createInvoice = useCreateInvoice();
  const voidInvoice = useVoidInvoice();
  const reconcile = useReconcile();
  const accountDetail = useAccountDetail();
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const cycleParams = useMemo(
    () =>
      selectedCycle
        ? {
            from: selectedCycle.from,
            to: selectedCycle.to,
            referenceTime: selectedCycle.started_at,
            futurePreview: selectedCycle.is_preview,
          }
        : undefined,
    [selectedCycle],
  );

  const refreshAccounts = useCallback(() => {
    if (!selectedCycle) return;
    cycleAccounts.fetch({
      from: selectedCycle.from,
      to: selectedCycle.to,
      referenceTime: selectedCycle.started_at,
      futurePreview: selectedCycle.is_preview,
    });
  }, [selectedCycle, cycleAccounts.fetch]);

  useEffect(() => {
    cycles.fetch();
  }, [cycles.fetch]);

  useEffect(() => {
    if (selectedCycle) {
      refreshAccounts();
    }
  }, [selectedCycle, refreshAccounts]);

  useEffect(() => {
    if (!reconcileState) return;

    reconcile.fetchPreview(reconcileState.accountId, cycleParams);
  }, [cycleParams, reconcile.fetchPreview, reconcileState]);

  useEffect(() => {
    if (!detailState) return;

    accountDetail.inspectAccount(
      detailState.account_id,
      flattenAccountTimesheets(detailState).map((timesheet) => timesheet.id),
    );
  }, [accountDetail.inspectAccount, detailState]);

  const handleCycleSelect = useCallback(
    (cycle: InvoiceCycle) => {
      cycleAccounts.clear();
      setSelectedCycle(cycle);
      setSearchQuery('');
      setAccountFilter('all');
      setConfirmCreateState(null);
      setReconcileState(null);
      setDetailState(null);
      assignTimesheets.clearError();
      removeLineItem.clearError();
      createInvoice.clearError();
      voidInvoice.clearError();
      reconcile.clear();
      accountDetail.clear();
    },
    [
      accountDetail.clear,
      assignTimesheets.clearError,
      cycleAccounts.clear,
      cycleAnalysis.clear,
      removeLineItem.clearError,
      createInvoice.clearError,
      voidInvoice.clearError,
      reconcile.clear,
    ],
  );

  const handleAssignTimesheets = useCallback(
    async (
      _account: CycleAccount,
      invoiceId: number,
      timesheetIds: number[],
    ) => {
      assignTimesheets.clearError();
      const result = await assignTimesheets.assign({ invoiceId, timesheetIds });

      if (result.success) {
        refreshAccounts();
        return true;
      }

      return false;
    },
    [assignTimesheets.assign, assignTimesheets.clearError, refreshAccounts],
  );

  const handleRemoveLineItem = useCallback(
    async (_account: CycleAccount, timesheet: CycleTimesheet) => {
      if (!timesheet.invoice_line_item_id) return;

      const confirmed = window.confirm(
        `Remove line item for timesheet #${timesheet.id} (${timesheet.worker_name || 'Unknown'})?\n\nThis will delete the line item from Stripe and update the invoice total.`,
      );
      if (!confirmed) return;

      removeLineItem.clearError();
      const result = await removeLineItem.remove(
        timesheet.invoice_line_item_id,
      );

      if (result.success) {
        refreshAccounts();
      } else {
        window.alert(result.error || 'Failed to remove line item');
      }
    },
    [removeLineItem.remove, removeLineItem.clearError, refreshAccounts],
  );

  const handleCreateInvoice = useCallback(
    async (account: CycleAccount) => {
      if (!selectedCycle) return;

      createInvoice.clearError();
      const result = await createInvoice.fetchPreview({
        accountId: account.account_id,
        referenceTime: selectedCycle.started_at,
        from: selectedCycle.from,
        to: selectedCycle.to,
        futurePreview: selectedCycle.is_preview,
      });

      if (result.success) {
        setConfirmCreateState({ account });
      }
    },
    [selectedCycle, createInvoice.fetchPreview, createInvoice.clearError],
  );

  const handleConfirmCreateInvoice = useCallback(
    async (groupKeys: string[]) => {
      if (!selectedCycle || !confirmCreateState) return;

      const result = await createInvoice.create({
        accountId: confirmCreateState.account.account_id,
        referenceTime: selectedCycle.started_at,
        from: selectedCycle.from,
        to: selectedCycle.to,
        futurePreview: selectedCycle.is_preview,
        groupKeys,
      });

      if (result.success) {
        setConfirmCreateState(null);
        refreshAccounts();
      }
    },
    [confirmCreateState, createInvoice.create, refreshAccounts, selectedCycle],
  );

  const handleVoidInvoice = useCallback(
    async (account: CycleAccount, invoice: CycleInvoiceSection) => {
      const confirmed = window.confirm(
        `Void invoice #${invoice.id} for ${account.account_name}? This will void the invoice in Stripe and reset linked timesheets.`,
      );
      if (!confirmed) return;

      voidInvoice.clearError();
      const success = await voidInvoice.voidInvoice(invoice.id);

      if (success) {
        refreshAccounts();
      }
    },
    [voidInvoice.voidInvoice, voidInvoice.clearError, refreshAccounts],
  );

  const handleInspectInvoice = useCallback(
    (account: CycleAccount) => {
      accountDetail.clear();
      setDetailState(account);
    },
    [accountDetail.clear],
  );

  const handleDetailClose = useCallback(() => {
    accountDetail.clear();
    setDetailState(null);
  }, [accountDetail.clear]);

  const handleReconcile = useCallback(
    (account: CycleAccount) => {
      reconcile.clear();
      setReconcileState({
        accountId: account.account_id,
        accountName: account.account_name,
      });
    },
    [reconcile.clear],
  );

  const handleReconcileClose = useCallback(() => {
    if (reconcile.executing) return;

    reconcile.clear();
    setReconcileState(null);
  }, [reconcile.clear, reconcile.executing]);

  const handleReconcileExecute = useCallback(async () => {
    if (!reconcileState) return;

    const executeResult = await reconcile.execute(
      reconcileState.accountId,
      cycleParams,
    );

    if (executeResult.success) {
      refreshAccounts();
    }
  }, [cycleParams, reconcile.execute, reconcileState, refreshAccounts]);

  const handleRunCycleAnalysis = useCallback(async () => {
    if (!selectedCycle) return;

    cycleAnalysis.clear();
    setCycleAnalysisState({ cycleLabel: selectedCycle.date_label });
    await cycleAnalysis.fetch({
      from: selectedCycle.from,
      to: selectedCycle.to,
      referenceTime: selectedCycle.started_at,
      futurePreview: selectedCycle.is_preview,
    });
  }, [cycleAnalysis.clear, cycleAnalysis.fetch, selectedCycle]);

  const handleCloseCycleAnalysis = useCallback(() => {
    cycleAnalysis.clear();
    setCycleAnalysisState(null);
  }, [cycleAnalysis.clear]);

  const accounts = useMemo(
    () => cycleAccounts.data?.accounts || [],
    [cycleAccounts.data],
  );
  const filteredAccounts = useMemo(() => {
    const normalizedQuery = deferredSearchQuery.trim().toLowerCase();
    return accounts.filter((account) => {
      const accountName = account.account_name.toLowerCase();
      const customerName = account.customer_name.toLowerCase();
      const accountId = String(account.account_id);
      const matchesSearch =
        accountName.includes(normalizedQuery) ||
        customerName.includes(normalizedQuery) ||
        accountId.includes(normalizedQuery);

      if (!matchesSearch) return false;

      if (accountFilter === 'all') return true;
      if (accountFilter === 'no_invoice') return account.invoices.length === 0;

      const actionableMissing = account.unassigned_timesheets.filter(
        (ts) => ts.status === 'not_included',
      ).length;
      const isComplete =
        account.invoices.length > 0 &&
        (account.conflicted_groups || []).length === 0 &&
        actionableMissing === 0;

      if (accountFilter === 'complete') return isComplete;
      // Incomplete = has invoice but not complete, OR has no invoice
      return !isComplete;
    });
  }, [accounts, accountFilter, deferredSearchQuery]);
  const actionError =
    assignTimesheets.error ||
    removeLineItem.error ||
    createInvoice.error ||
    voidInvoice.error;
  const isRefreshingCycleAccounts =
    selectedCycle != null &&
    cycleAccounts.loading &&
    cycleAccounts.data != null;

  const handlePasswordSubmit = useCallback(
    (event?: React.FormEvent | React.MouseEvent) => {
      if (event) event.preventDefault();

      if (password === INVOICE_HEALTH_PASSWORD) {
        setIsAuthenticated(true);
        setAuthError('');
        localStorage.setItem(INVOICE_HEALTH_AUTH_KEY, 'true');
        return;
      }

      setAuthError('Incorrect password');
      setPassword('');
    },
    [password],
  );

  if (!isAuthenticated) {
    return (
      <Page title="Invoice Health" size="sm">
        <Card>
          <Card.Section>
            <form onSubmit={handlePasswordSubmit}>
              <Stack vertical gap={16}>
                <div>
                  <Heading>Invoice Health</Heading>
                  <Small>Enter the page password to continue.</Small>
                </div>
                <div>
                  <input
                    autoFocus
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Enter password"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: 8,
                      border: '1px solid #d0d5dd',
                      background: '#fff',
                      fontSize: 14,
                      color: '#344054',
                    }}
                  />
                </div>
                {authError && (
                  <div style={{ color: '#b42318', fontSize: 14 }}>
                    {authError}
                  </div>
                )}
                <Stack justify="end">
                  <Button
                    a11yLabel="Unlock invoice health"
                    label="Continue"
                    type="submit"
                    onClick={handlePasswordSubmit}
                  />
                </Stack>
              </Stack>
            </form>
          </Card.Section>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Invoice Health" size="xl">
      <Stack vertical gap={20}>
        <Card>
          <Card.Section>
            <Stack vertical gap={12}>
              <div>
                <Heading>Invoice Health</Heading>
                <Small>
                  Select a cycle to review approved timesheets by account
                </Small>
              </div>

              {cycles.loading ? (
                <Skeleton.Body lines={2} />
              ) : cycles.error ? (
                <div style={{ color: '#b42318', fontSize: 14 }}>
                  {cycles.error}
                </div>
              ) : (
                <Stack vertical gap={12}>
                  <CycleDropdown
                    cycles={cycles.cycles}
                    cycleData={cycleAccounts.data}
                    selectedCycle={selectedCycle}
                    onSelect={handleCycleSelect}
                  />
                  {selectedCycle && (
                    <Stack gap={12} wrap>
                      <Button
                        a11yLabel="Run invoice analysis"
                        appearance="secondary"
                        label="Run invoice analysis"
                        isLoading={cycleAnalysis.loading}
                        type="button"
                        onClick={handleRunCycleAnalysis}
                      />
                      <input
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search account id, account name, or customer name"
                        style={{
                          width: '100%',
                          maxWidth: 420,
                          padding: '10px 14px',
                          borderRadius: 8,
                          border: '1px solid #d0d5dd',
                          background: '#fff',
                          fontSize: 14,
                          color: '#344054',
                        }}
                      />
                      <select
                        value={accountFilter}
                        onChange={(event) =>
                          setAccountFilter(event.target.value as AccountFilter)
                        }
                        style={{
                          width: '100%',
                          maxWidth: 220,
                          padding: '10px 14px',
                          borderRadius: 8,
                          border: '1px solid #d0d5dd',
                          background: '#fff',
                          fontSize: 14,
                          fontWeight: 500,
                          color: '#344054',
                          cursor: 'pointer',
                          appearance: 'none',
                        }}
                      >
                        <option value="all">All statuses</option>
                        <option value="complete">Complete</option>
                        <option value="incomplete">Incomplete</option>
                        <option value="no_invoice">No invoice</option>
                      </select>
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          </Card.Section>
        </Card>

        {actionError && (
          <Card>
            <Card.Section>
              <div style={{ color: '#b42318', fontSize: 14 }}>
                {actionError}
              </div>
            </Card.Section>
          </Card>
        )}

        {!selectedCycle && !cycles.loading && (
          <Card>
            <Card.Section>
              <EmptyState title="Select a cycle to load accounts and approved timesheets" />
            </Card.Section>
          </Card>
        )}

        {selectedCycle && cycleAccounts.loading && !cycleAccounts.data && (
          <LoadingState text="Loading accounts and timesheets..." />
        )}

        {isRefreshingCycleAccounts && selectedCycle && (
          <Card>
            <Card.Section>
              <LoadingState
                overlayColor="white"
                text={`Refreshing ${selectedCycle.date_label} accounts and timesheets...`}
                css={{ minHeight: 120 }}
              />
            </Card.Section>
          </Card>
        )}

        {selectedCycle && cycleAccounts.error && (
          <Card>
            <Card.Section>
              <div style={{ color: '#b42318', fontSize: 14 }}>
                {cycleAccounts.error}
              </div>
            </Card.Section>
          </Card>
        )}

        {selectedCycle && cycleAccounts.data && (
          <>
            {accounts.length === 0 ? (
              <Card>
                <Card.Section>
                  <EmptyState title="No approved timesheets found in this cycle" />
                </Card.Section>
              </Card>
            ) : filteredAccounts.length === 0 ? (
              <Card>
                <Card.Section>
                  <EmptyState title="No accounts match that search" />
                </Card.Section>
              </Card>
            ) : (
              <Stack vertical gap={20}>
                {filteredAccounts.map((account) => (
                  <AccountCard
                    key={account.account_id}
                    account={account}
                    loadingAssignInvoiceId={assignTimesheets.loadingInvoiceId}
                    loadingRemoveItemId={removeLineItem.loadingItemId}
                    loadingCreateAccountId={createInvoice.loadingAccountId}
                    loadingVoidInvoiceId={voidInvoice.loadingInvoiceId}
                    onAssignTimesheets={handleAssignTimesheets}
                    onRemoveLineItem={handleRemoveLineItem}
                    onInspectInvoice={handleInspectInvoice}
                    onCreateInvoice={handleCreateInvoice}
                    onVoidInvoice={handleVoidInvoice}
                    onReconcile={handleReconcile}
                  />
                ))}
              </Stack>
            )}
          </>
        )}

        <ReconcileModal
          isOpen={reconcileState != null}
          accountName={reconcileState?.accountName || null}
          accountId={reconcileState?.accountId || null}
          preview={reconcile.preview}
          loading={reconcile.loading}
          executing={reconcile.executing}
          error={reconcile.error}
          onClose={handleReconcileClose}
          onExecute={handleReconcileExecute}
        />
        <ConfirmCreateInvoiceModal
          state={confirmCreateState}
          groups={createInvoice.groups}
          loading={createInvoice.loadingAccountId != null}
          error={createInvoice.error}
          onCancel={() => {
            createInvoice.clearError();
            setConfirmCreateState(null);
          }}
          onConfirm={handleConfirmCreateInvoice}
        />
        <AccountDetailModal
          isOpen={detailState != null}
          loading={accountDetail.loading}
          error={accountDetail.error}
          account={detailState}
          accountDetail={accountDetail.accountDetail}
          onClose={handleDetailClose}
        />
        <CycleAnalysisModal
          isOpen={cycleAnalysisState != null}
          cycleLabel={cycleAnalysisState?.cycleLabel || null}
          loading={cycleAnalysis.loading}
          error={cycleAnalysis.error}
          analysis={cycleAnalysis.analysis}
          onClose={handleCloseCycleAnalysis}
        />
      </Stack>
    </Page>
  );
};

export default InvoiceHealth;
